import * as React from 'react'
import { GridPrivateApiCommon } from '../../../models/api/gridApiCommon'
import { useGridApiMethod } from '../../utils/useGridApiMethod'
import { useGridApiEventHandler } from '../../utils/useGridApiEventHandler'
import { DataGridProcessedProps } from '../../../models/props/DataGridProps'
import {
  ReactTableRowId,
  RowSelectionState,
} from '../../../models/gridReactTable'
import {
  GridRowMultiSelectionApi,
  GridRowSelectionApi,
} from '../../../models/api/gridRowSelectionApi'
import { isKeyboardEvent } from '../../../utils/keyboardUtils'
import { GridEventListener } from '../../../models'
import { GRID_CHECKBOX_SELECTION_FIELD } from '../../../colDef/gridCheckboxSelectionColDef'

export const useGridRowSelection = (
  apiRef: React.MutableRefObject<GridPrivateApiCommon>,
  props: Pick<
    DataGridProcessedProps,
    | 'disableMultipleRowSelection'
    | 'disableRowSelectionOnClick'
    | 'disableMultipleRowSelectionWithCheckboxSelection'
    | 'checkboxSelection'
    | 'rowSelection'
  >
): void => {
  const runIfRowSelectionIsEnabled =
    <Args extends any[]>(callback: (...args: Args) => void) =>
    (...args: Args) => {
      if (props.rowSelection) {
        callback(...args)
      }
    }

  const {
    disableMultipleRowSelection,
    disableRowSelectionOnClick,
    disableMultipleRowSelectionWithCheckboxSelection,
    checkboxSelection,
  } = props

  const lastRowToggled = React.useRef<ReactTableRowId | null>(null)

  const canHaveMultipleSelection =
    !disableMultipleRowSelection || checkboxSelection

  const expandMouseRowRangeSelection = React.useCallback(
    (id: ReactTableRowId) => {
      const visibleRowsById =
        apiRef.current.tableInstance.getCoreRowModel().rowsById
      const visibleRows =
        apiRef.current.tableInstance.getCoreRowModel().flatRows
      let endId: string | undefined = id

      const startId = lastRowToggled.current ?? id
      const isSelected = visibleRowsById[id]?.getIsSelected()

      if (isSelected) {
        const startIndex = visibleRowsById[startId]?.index
        const endIndex = visibleRowsById[endId]?.index
        if (startIndex === endIndex) {
          return
        }
        if (startIndex! > endIndex!) {
          endId = visibleRows[endIndex! + 1]?.id
        } else {
          endId = visibleRows[endIndex! - 1]?.id
        }
      }
      lastRowToggled.current = id
      // @ts-ignore
      apiRef.current.selectRowRange({ startId, endId }, !isSelected)
    },
    [apiRef]
  )

  /**
   * API Methods
   */

  const selectRow = React.useCallback<GridRowSelectionApi['selectRow']>(
    (id, isSelected = true, resetSelection = false) => {
      const visibleRowsById =
        apiRef.current.tableInstance.getCoreRowModel().rowsById
      if (!visibleRowsById[id]?.getCanSelect()) {
        return
      }

      lastRowToggled.current = id

      if (resetSelection) {
        apiRef.current.tableInstance.setRowSelection(
          isSelected ? { [id]: true } : {}
        )
      } else {
        const newSelection = Object.keys(
          apiRef.current.tableInstance.getState().rowSelection
        ).filter(one => one !== id)

        if (isSelected) {
          newSelection.push(id)
        }

        const isSelectionValid =
          newSelection.length < 2 || canHaveMultipleSelection

        if (isSelectionValid) {
          const values = newSelection.reduce((prev, curr) => {
            prev[curr] = true
            return prev
          }, {} as RowSelectionState)
          apiRef.current.tableInstance.setRowSelection(values)
        }
      }
    },
    [apiRef, canHaveMultipleSelection]
  )

  const selectRows = React.useCallback<GridRowMultiSelectionApi['selectRows']>(
    (ids, isSelected = true, resetSelection = false) => {
      const visibleRowsById =
        apiRef.current.tableInstance.getCoreRowModel().rowsById
      const selectableIds = ids.filter(id =>
        visibleRowsById[id]?.getCanSelect()
      )

      let newSelection: ReactTableRowId[]
      if (resetSelection) {
        newSelection = isSelected ? selectableIds : []
      } else {
        const selectionLookup = {
          ...apiRef.current.tableInstance.getState().rowSelection,
        }

        selectableIds.forEach(id => {
          if (isSelected) {
            selectionLookup[id] = true
          } else {
            delete selectionLookup[id]
          }
        })
        newSelection = Object.keys(selectionLookup)
      }
      const isSelectionValid =
        newSelection.length < 2 || canHaveMultipleSelection

      if (isSelectionValid) {
        const values = newSelection.reduce((prev, curr) => {
          prev[curr] = true
          return prev
        }, {} as RowSelectionState)
        apiRef.current.tableInstance.setRowSelection(values)
      }
    },
    [apiRef, canHaveMultipleSelection]
  )

  const selectRowRange = React.useCallback<
    GridRowMultiSelectionApi['selectRowRange']
  >(
    ({ startId, endId }, isSelected, resetSelection) => {
      const visibleRowsById =
        apiRef.current.tableInstance.getCoreRowModel().rowsById
      const visibleRows =
        apiRef.current.tableInstance.getCoreRowModel().flatRows

      const startIndex = visibleRowsById[startId]!.index!
      const endIndex = visibleRowsById[endId]!.index!
      const [start, end] =
        startIndex > endIndex ? [endIndex, startIndex] : [startIndex, endIndex]
      const rowsBetweenStartAndEnd = visibleRows
        .slice(start, end + 1)
        .map(one => one.id)

      apiRef.current.selectRows(
        rowsBetweenStartAndEnd,
        isSelected,
        resetSelection
      )
    },
    [apiRef]
  )

  const isRowSelected = React.useCallback<GridRowSelectionApi['isRowSelected']>(
    id => {
      const visibleRowsById =
        apiRef.current.tableInstance.getCoreRowModel().rowsById
      return visibleRowsById[id]!.getIsSelected()
    },
    [apiRef]
  )

  const isRowSelectable = React.useCallback<
    GridRowSelectionApi['isRowSelectable']
  >(
    id => {
      const visibleRowsById =
        apiRef.current.tableInstance.getCoreRowModel().rowsById
      return visibleRowsById[id]!.getCanSelect()
    },
    [apiRef]
  )

  const selectionApi: GridRowSelectionApi = {
    selectRow,
    isRowSelectable,
    isRowSelected,
  }

  const multiSelectionApi: GridRowMultiSelectionApi = {
    selectRows,
    selectRowRange,
  }

  useGridApiMethod(apiRef, selectionApi, 'public')
  useGridApiMethod(apiRef, multiSelectionApi, 'public')

  /**
   * Events
   */

  const handleSingleRowSelection = React.useCallback(
    (id: ReactTableRowId, event: React.MouseEvent | React.KeyboardEvent) => {
      const hasCtrlKey = event.metaKey || event.ctrlKey

      // multiple selection is only allowed if:
      // - it is a checkboxSelection
      // - it is a keyboard selection
      // - Ctrl is pressed

      const isMultipleSelectionDisabled =
        !checkboxSelection && !hasCtrlKey && !isKeyboardEvent(event)

      const resetSelection =
        !canHaveMultipleSelection || isMultipleSelectionDisabled

      const isSelected = apiRef.current.isRowSelected(id)

      if (resetSelection) {
        apiRef.current.selectRow(
          id,
          !isMultipleSelectionDisabled ? !isSelected : true,
          true
        )
      } else {
        apiRef.current.selectRow(id, !isSelected, false)
      }
    },
    [apiRef, canHaveMultipleSelection, checkboxSelection]
  )

  const handleCellClick = React.useCallback<GridEventListener<'cellClick'>>(
    (params, event) => {
      if (disableRowSelectionOnClick) {
        return
      }

      if (params.field === GRID_CHECKBOX_SELECTION_FIELD) {
        return
      }
      if (disableMultipleRowSelectionWithCheckboxSelection) {
        return
      }

      if (event.shiftKey && (canHaveMultipleSelection || checkboxSelection)) {
        expandMouseRowRangeSelection(params.rowId)
      } else {
        handleSingleRowSelection(params.rowId, event)
      }
    },
    [
      disableRowSelectionOnClick,
      expandMouseRowRangeSelection,
      handleSingleRowSelection,
      canHaveMultipleSelection,
      checkboxSelection,
      disableMultipleRowSelectionWithCheckboxSelection,
    ]
  )

  const preventSelectionOnShift = React.useCallback<
    GridEventListener<'cellMouseDown'>
  >(
    (params, event) => {
      if (canHaveMultipleSelection && event.shiftKey) {
        window.getSelection()?.removeAllRanges()
      }
    },
    [canHaveMultipleSelection]
  )

  const handleRowSelectionCheckboxChange = React.useCallback<
    GridEventListener<'rowSelectionCheckboxChange'>
  >(
    (params, event) => {
      if (disableMultipleRowSelectionWithCheckboxSelection) {
        let value = params.value
        if (value === false) {
          value = true
        }
        apiRef.current.selectRow(params.id, value, true)
        return
      }
      if ((event.nativeEvent as any).shiftKey) {
        expandMouseRowRangeSelection(params.id)
      } else {
        apiRef.current.selectRow(params.id, params.value)
      }
    },
    [
      apiRef,
      expandMouseRowRangeSelection,
      disableMultipleRowSelectionWithCheckboxSelection,
    ]
  )

  useGridApiEventHandler(
    apiRef,
    'cellClick',
    runIfRowSelectionIsEnabled(handleCellClick)
  )
  useGridApiEventHandler(
    apiRef,
    'rowSelectionCheckboxChange',
    runIfRowSelectionIsEnabled(handleRowSelectionCheckboxChange)
  )
  useGridApiEventHandler(
    apiRef,
    'cellMouseDown',
    runIfRowSelectionIsEnabled(preventSelectionOnShift)
  )
}
